import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class SvgCacheService {
    private readonly _httpClient = inject(HttpClient);

    public svgIconMap = new Map<string, string>();

    public fetchSvgFromPath(filePath: string, fallbackSvgPath: string, autoSize = false): Observable<string> {
        const cachedSvg = this.svgIconMap.get(filePath);
        if (cachedSvg) {
            return of(cachedSvg);
        }

        let noCache = false;
        return this._httpClient
            .get(filePath, {
                headers: new HttpHeaders().set('Content-Type', 'image/svg+xml'),
                responseType: 'text',
            })
            .pipe(
                catchError(() => {
                    noCache = true;
                    return this._fetchGenericFallbackSvg(fallbackSvgPath);
                }),
                map(svgString => {
                    const content = this._fixAndSanitizeSvg(filePath, svgString, autoSize);
                    if (!noCache) {
                        this.svgIconMap.set(filePath, content);
                    }
                    return content;
                }),
            );
    }

    private _fixAndSanitizeSvg(filePath: string, svgContent: string, autoSize: boolean): string {
        const filePathHash = btoa(filePath);
        let fixedSvg = this._fixSvgInlineStyles(svgContent, filePathHash);
        if (autoSize) {
            fixedSvg = this._removeSvgSizes(fixedSvg);
            fixedSvg = fixedSvg.replace('<svg ', '<svg class="auto-size" ');
        }

        return fixedSvg;
    }

    private _fetchGenericFallbackSvg(fallbackSvgPath: string): Observable<string> {
        return this._httpClient.get(fallbackSvgPath, {
            headers: new HttpHeaders().set('Content-Type', 'image/svg+xml'),
            responseType: 'text',
        });
    }

    private _fixSvgInlineStyles(svgContent: string, filehash: string): string {
        const classRegex = /class="([\s\S]*?)"/gm;
        const svgClassStrings = new Set<string>();

        let tmpMatch: RegExpExecArray | null;
        while ((tmpMatch = classRegex.exec(svgContent))) {
            if (tmpMatch != null) {
                svgClassStrings.add(tmpMatch[1]);
            }
        }

        // return early if no classes recognized
        if (svgClassStrings.size <= 0) {
            return svgContent;
        }

        // fix filehash, it will be placed in html + css so it can not contain weird chars
        filehash = filehash.replace(/[\^°!"²§³$%&/{([)\]=}?\\´`@€+*~#'<>|,;.:\-_]/g, '');

        svgClassStrings.forEach(classString => {
            classString = classString.trim();
            classString.split(' ').forEach(className => {
                if (className != null) {
                    className = className.trim();
                    const newClassName = `${className}-${filehash}`;

                    // replace all occurrences
                    svgContent = svgContent.replace(new RegExp(className, 'g'), newClassName);

                    // fix class
                    svgContent = svgContent.replace(
                        // eslint-disable-next-line no-useless-escape
                        new RegExp(`class="[\s]*${newClassName}`, 'g'),
                        `class="${className} ${newClassName}`,
                    );
                }
            });
        });

        return svgContent;
    }

    private _removeSvgSizes(svgContent: string): string {
        // eslint-disable-next-line sonarjs/slow-regex
        const widthRegex = /<svg.*?(width=".*?").*?>/gm;
        const widthMatches = widthRegex.exec(svgContent);
        if (widthMatches != null && widthMatches.length > 1) {
            svgContent = svgContent.replace(widthMatches[1], '');
        }

        // eslint-disable-next-line sonarjs/slow-regex
        const heightRegex = /<svg.*?(height=".*?").*?>/gm;
        const heightMatches = heightRegex.exec(svgContent);
        if (heightMatches != null && heightMatches.length > 1) {
            svgContent = svgContent.replace(heightMatches[1], '');
        }

        return svgContent;
    }
}
